import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Redirect, RouteComponentProps } from "react-router-dom";
import { useAppSelector } from "../../hooks";
import useCryptography from "../../hooks/useCryptography";
import { useRouter } from "../../hooks/useRouter";
import { useViewport } from "../../hooks/useViewport";
import { useWeb3Auth } from "../../hooks/useWeb3Auth";
import { ROUTES } from "../../routes";
import { updateRoomMessages } from "../../store/reducers/dmState";
import { KeypairState, TMessage } from "../../types/types";
import { getProticoClient } from "../../utils/api";
import { notEmpty } from "../../utils/format.utils";
import Chat from "./Chat";
import ChatListItem from "./ChatListItem";
import "./index.css";

interface MatchParams {
  toAddress?: string;
}

interface Props extends RouteComponentProps<MatchParams> {}

const DMPage: FC<Props> = ({ match }) => {
  const { query }: { [key: string]: any } = useRouter();
  const { keypairState } = useAppSelector((state) => state.keys);
  const { logined } = useWeb3Auth();
  const { toAddress } = match.params;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const { currentAddress } = useAppSelector((state) => state.auth);
  const { roomInformations } = useAppSelector((state) => state.dmState);
  const { width } = useViewport();

  const lastMessageList = useMemo(() => {
    let results: Array<TMessage> = [];

    for (const [_, roomInformation] of Object.entries(roomInformations)) {
      let messages: Array<TMessage> = [];
      for (const [id, message] of Object.entries(roomInformation.messageList)) {
        messages.push(message);
      }
      messages.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      if (messages.length > 0) results.push(messages[0]);
    }
    return results.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [roomInformations]);
  const { userBlockList } = useAppSelector((state) => state.profile);
  const { decrypt } = useCryptography();

  const getMessages = () => {
    setIsFetching(true);
    getProticoClient()
      .directMessages.getPreview(currentAddress)
      .then((historyMessageList) => {
        const processedMessages = historyMessageList
          .map((historyMessage) => {
            try {
              const plaintextMessage = decrypt(historyMessage);
              return {
                ...historyMessage,
                plaintextMessage: plaintextMessage,
              } as TMessage;
            } catch (_) {
              return undefined;
            }
          })
          .filter(notEmpty)
          .filter(
            (message) =>
              !userBlockList.includes(message.fromAddress) &&
              !userBlockList.includes(message.toAddress)
          );
        processedMessages.forEach((message) => {
          dispatch(
            updateRoomMessages({
              toAddress:
                currentAddress === message.fromAddress
                  ? message.toAddress
                  : message.fromAddress,
              message: { ...message, sended: true },
            })
          );
        });
        setIsFetching(false);
      });
  };

  useEffect(() => {
    getMessages();
  }, []);

  const getMenuView = useMemo(() => {
    if (isFetching)
      return (
        <div className="body-container">
          <div className="dot-falling" />
        </div>
      );
    if (lastMessageList.length === 0)
      return <div className="body-container">{t("DM_No_Data")}</div>;

    return (
      <ul>
        {lastMessageList.map((message, index) => {
          return (
            <ChatListItem
              key={`FriendItem_${index}`}
              message={message}
              toAddress={toAddress}
            />
          );
        })}
      </ul>
    );
  }, [isFetching, lastMessageList, toAddress]);
  if (!logined)
    return (
      <Redirect
        to={{
          pathname: ROUTES.LOGIN_PAGE,
        }}
      />
    );
  if (keypairState === KeypairState.WAITING_KEY_AUTH)
    return (
      <Redirect
        to={{ pathname: ROUTES.KEYPAIR_PAGE, state: { from: ROUTES.DM_PAGE } }}
      />
    );

  // return <React.Fragment>{getview}</React.Fragment>;
  return (
    <div className="dm-container">
      {!!lastMessageList.length && (
        <>
          {width > 650 ? (
            <div
              className="main-menu"
              style={{
                width: "250px",
              }}
            >
              {getMenuView}
            </div>
          ) : (
            <div className="main-menu">{getMenuView}</div>
          )}
        </>
      )}
      <div
        className="dm-sub-container"
        style={{
          ...(lastMessageList.length === 0 && {
            width: "initial",
            left: "initial",
            maxWidth: "100%",
          }),
        }}
      >
        {!!toAddress ? (
          <Chat toAddress={toAddress} alias={query?.alias} />
        ) : (
          <div
            style={{
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            {lastMessageList.length === 0 ? (
              <h3>{t("DM_No_Data")}</h3>
            ) : (
              <>
                <h3>{t("DM_Your_Message")}</h3>
                <p>{t("DM_Index_Message")}</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
export default DMPage;
